import type { ProcedureDef } from '../procedure';
import { FAMILY_NAMES, type FamilyDef, type FamilyName } from './family';
import { adminFamily, adminProcedures } from './admin';
import { analyseFamily, analyseProcedures } from './analyse';
import { authFamily, authProcedures } from './auth';
import { billingFamily, billingProcedures } from './billing';
import { dashboardFamily, dashboardProcedures } from './dashboard';
import { engineFamily, engineProcedures } from './engine';
import { executeFamily, executeProcedures } from './execute';
import { manageFamily, manageProcedures } from './manage';
import { meFamily, meProcedures } from './me';
import { notificationsFamily, notificationsProcedures } from './notifications';
import { portalFamily, portalProcedures } from './portal';
import { prepareFamily, prepareProcedures } from './prepare';
import { publicFamily, publicProcedures } from './public';
import { reviewFamily, reviewProcedures } from './review';
import { voiceFamily, voiceProcedures } from './voice';

export * from './family';
export * from './auth';
export * from './me';
export * from './dashboard';
export * from './prepare';
export * from './execute';
export * from './review';
export * from './analyse';
export * from './manage';
export * from './billing';
export * from './notifications';
export * from './voice';
export * from './engine';
export * from './admin';
export * from './public';
export * from './portal';

/** Registry of the 15 families, keyed by name (DEC-009). */
export const families = {
  auth: authFamily,
  me: meFamily,
  dashboard: dashboardFamily,
  prepare: prepareFamily,
  execute: executeFamily,
  review: reviewFamily,
  analyse: analyseFamily,
  manage: manageFamily,
  billing: billingFamily,
  notifications: notificationsFamily,
  voice: voiceFamily,
  engine: engineFamily,
  admin: adminFamily,
  public: publicFamily,
  portal: portalFamily,
} as const satisfies Record<FamilyName, FamilyDef>;
export type Families = typeof families;
export const familyList: readonly FamilyDef[] = FAMILY_NAMES.map((n) => families[n]);

/** Every procedure of every family, flattened: `'domain.module.verb' → { input, output, kind, summary }`. */
export const allProcedures = {
  ...authProcedures,
  ...meProcedures,
  ...dashboardProcedures,
  ...prepareProcedures,
  ...executeProcedures,
  ...reviewProcedures,
  ...analyseProcedures,
  ...manageProcedures,
  ...billingProcedures,
  ...notificationsProcedures,
  ...voiceProcedures,
  ...engineProcedures,
  ...adminProcedures,
  ...publicProcedures,
  ...portalProcedures,
} as const;
export type AllProcedures = typeof allProcedures;
export type ProcedureNameOf = keyof AllProcedures;

/** Which family owns a procedure name. */
export function familyOfProcedure(name: string): FamilyName | null {
  for (const fam of familyList) if (name in fam.procedures) return fam.name;
  return null;
}
export function procedureDef(name: string): ProcedureDef | null {
  return (allProcedures as Record<string, ProcedureDef | undefined>)[name] ?? null;
}
